import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { MfaChallenge } from '../../services/mfa-challenge';
import { Router, ActivatedRoute } from '@angular/router';
import { NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import { skipWhile, take, timeout } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import firebase from 'firebase/app';

export enum LoginMode {
  Login = 0,
  PasswordReset = 1,
  Signup = 2,
  MfaChallenge = 3,
  RevertSecondFactor = 4,
}

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {

  /**
   * Host for the invisible reCAPTCHA widget. Deliberately at the template root, outside
   * every mode's *ngIf, so it survives the switch from the login form to the challenge -
   * which is also why it resolves statically rather than per view.
   */
  @ViewChild('recaptchaHost', { static: true })
  public recaptchaHost: ElementRef<HTMLElement>;

  public username: string;

  public password: string;

  public invalidLogin: string = null;

  public oobCode: string;

  public querySub: Subscription;

  public user: string;

  public token: string;

  public mode = LoginMode.Login;

  public modes = LoginMode;

  public mfaError: string = null;

  public requestInProgress = false;

  /**
   * The SMS challenge that stands between a correct password and a session on an account
   * with a second factor. Owns the code step's state, the fresh reCAPTCHA per send, the
   * NgZone re-entry, and the generation counter that lets Back to Login stay usable while
   * a request is in flight: a completion arriving after the user left is stale and no-ops
   * rather than reviving a screen nobody is looking at.
   */
  public mfa: MfaChallenge;

  private mfaResolver: firebase.auth.MultiFactorResolver;

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private ngZone: NgZone,
  ) {
    this.mfa = new MfaChallenge(this.authService, this.ngZone, () => this.recaptchaHost.nativeElement);
  }

  public ngOnInit(): void {
    this.querySub = this.route
      .queryParams
      .subscribe(() => {
        this.mode = LoginMode.Login;
        this.oobCode = this.token = this.getParamValue('oobCode');
        if (this.oobCode) {
          // Firebase email action links share this page as their action URL and identify
          // the action in the "mode" query param. Links without one are password resets
          // (the only action the app handled historically).
          this.mode = this.getParamValue('mode') === 'revertSecondFactorAddition'
            ? LoginMode.RevertSecondFactor
            : LoginMode.PasswordReset;
        }

        // Do we have a new user signup
        // Check first if we have a signup code.
        // Note that if you reached this page, you probably do not have any session
        // this.user = this.route.snapshot.queryParamMap.get('user');
        this.user = this.getParamUser();
        this.token = this.getParamValue('token');

        if (this.user && this.token) {
          this.mode = LoginMode.Signup;
        }
      });
  }

  public ngOnDestroy(): void {
    this.querySub.unsubscribe();
    // Nothing may deposit challenge state once the screen is gone, and the reCAPTCHA
    // widget has to be released with it.
    this.mfa.reset();
  }

  public login(form: NgForm): Promise<void> {
    if (form.invalid || this.requestInProgress) {
      return Promise.resolve();
    }

    this.invalidLogin = null;
    this.requestInProgress = true;
    // Captured before the password check, not inside the send: this is the await the
    // challenge's epoch guard exists to cover. Leaving the screen while the check is out
    // (browser Back, or a redirect from a session bootstrap) destroys the component and
    // resets the challenge, and the send that the rejection would otherwise start has to
    // be refused rather than text a code for a screen that no longer exists.
    const epoch = this.mfa.epoch;

    // Returned so one sign-in attempt, code send included, is a single awaitable chain.
    return this.authService
      .login(this.username, this.password)
      .toPromise()
      .then(() => this.completeLogin())
      .then(() => this.releaseRequest())
      .catch((e) => this.ngZone.run(() => {
        if (this.authService.isMfaRequired(e)) {
          // Leaves requestInProgress set: sendMfaCode() continues the same in-flight
          // request through the code send, so a second click can't land in the gap
          // between the password check resolving and the send actually starting.
          // Returned so the whole attempt, code send included, is one awaitable chain.
          return this.startMfaChallenge(e.resolver, epoch);
        }
        // The password check is the fifth surface Bug 47869 lists. A throttle here never
        // reaches the challenge - no send has been attempted - so it is handed over, which
        // takes Login out of service through canSend. Without it the button re-enables on
        // every refusal and each further click extends the block.
        this.mfa.noteThrottled(e, epoch);
        this.showInvalid(e);
        this.releaseRequest();
      }));
  }

  public async verifyMfaCode(form: NgForm): Promise<void> {
    if (form.invalid || this.requestInProgress) {
      return;
    }

    // required only rejects a truly empty value, so a whitespace-only entry still passes
    // it and reaches here - checked explicitly since Firebase validates the trimmed code
    // synchronously and throws rather than rejecting, which would otherwise escape the
    // catch below.
    const code = this.mfa.code;
    if (!code) {
      this.mfaError = 'Invalid verification code. Please check the text message and re-enter the code.';
      return;
    }

    this.mfaError = null;
    this.requestInProgress = true;
    const epoch = this.mfa.epoch;

    try {
      await this.authService.resolveMfaSignIn(this.mfaResolver, this.mfa.id, code);
      if (!this.mfa.isCurrent(epoch)) {
        // Cancelled while the verification was out. Firebase still completed the sign-in
        // on its end - nothing left for this screen, which is gone, to do.
        return;
      }
      await this.completeMfaLogin();
    } catch (e) {
      this.showVerifyError(e, epoch);
    } finally {
      if (this.mfa.isCurrent(epoch)) {
        this.releaseRequest();
      }
    }
  }

  /**
   * Sends a fresh code to the enrolled phone. The challenge retires the previous
   * verification id first, so a code typed while this is in flight cannot be checked
   * against an id Firebase has already replaced.
   */
  public async resendMfaCode(): Promise<void> {
    if (this.requestInProgress || !this.mfa.canResend) {
      return;
    }
    this.requestInProgress = true;
    this.mfaError = null;
    const epoch = this.mfa.epoch;
    try {
      await this.mfa.resend();
    } catch (e) {
      this.showSendError(e, epoch);
    } finally {
      if (this.mfa.isCurrent(epoch)) {
        this.releaseRequest();
      }
    }
  }

  public cancelMfaChallenge(): void {
    // reset() bumps the challenge's generation rather than waiting on anything: a verify
    // or send still in flight will complete server-side regardless, but its callbacks
    // compare their captured epoch first and no-op, so leaving never depends on - or waits
    // on - a request that may never settle.
    this.requestInProgress = false;
    this.mode = LoginMode.Login;
    this.password = null;
    this.mfaResolver = null;
    this.mfaError = null;
    this.mfa.reset();
  }

  /**
   * Takes the epoch the caller captured before its own await rather than reading it here,
   * so a challenge abandoned during that await refuses the send instead of finding the
   * epoch trivially current.
   */
  private startMfaChallenge(resolver: firebase.auth.MultiFactorResolver, epoch: number): Promise<void> {
    this.mfaResolver = resolver;
    this.mode = LoginMode.MfaChallenge;
    return this.sendMfaCode(
      this.authService.phoneHint(resolver), epoch);
  }

  private async sendMfaCode(phoneHint: string, epoch: number): Promise<void> {
    // Set (not just checked) here: this continues login()'s already-in-flight request, so
    // it must hold the flag through its own async round-trip rather than assume the caller
    // still owns it.
    this.requestInProgress = true;
    try {
      await this.mfa.send(
        (verifier) => this.authService.sendMfaChallengeCode(this.mfaResolver, verifier),
        { epoch, phoneHint });
    } catch (e) {
      this.showSendError(e, epoch);
    } finally {
      if (this.mfa.isCurrent(epoch)) {
        this.releaseRequest();
      }
    }
  }

  /**
   * NOTE: Firebase resolves its promises outside the Angular zone, so a message set after
   * an await has to re-enter the zone or the view will not show it.
   */
  private showVerifyError(e: any, epoch: number): void {
    console.error('MFA verification error', e);
    this.ngZone.run(() => {
      if (!this.mfa.isCurrent(epoch)) {
        return;
      }
      switch (e?.code) {
        case 'auth/invalid-verification-code':
          this.mfaError = 'Invalid verification code. Please check the text message and re-enter the code.';
          break;
        case 'auth/code-expired':
          this.mfaError = 'This code has expired. Click Resend Code to get a new one.';
          break;
        case 'auth/network-request-failed':
          this.mfaError = 'Network error. Please check your connection and try again.';
          break;
        default:
          this.mfaError = this.authService.mapMfaError(
            e, 'Unable to verify the code. Please try again or click Resend Code.');
          break;
      }
    });
  }

  /**
   * Same zone re-entry for a failed send. A send abandoned mid-flight resolves false
   * rather than rejecting, so anything landing here belongs to a live challenge - the
   * epoch is re-checked only to cover a cancel racing the rejection itself.
   */
  private showSendError(e: any, epoch: number): void {
    console.error('MFA code send error', e);
    this.ngZone.run(() => {
      if (!this.mfa.isCurrent(epoch)) {
        return;
      }
      this.mfaError = this.mapSendError(e);
    });
  }

  /**
   * grecaptcha and the RecaptchaVerifier constructor fail before any request leaves the
   * browser ("reCAPTCHA has already been rendered in this element", auth/argument-error),
   * and a reload is what clears that; a rejected send is worth retrying in place. Firebase
   * codes are how the two are told apart, so an errorless throw is treated as setup.
   */
  private mapSendError(e: any): string {
    if (!e?.code || e.code === 'auth/argument-error') {
      return this.authService.mapMfaError(
        e, 'Unable to start verification. Please reload the page and try again.');
    }
    return this.authService.mapMfaError(
      e, 'Unable to send the verification code. Click Resend Code to retry, or use Back to Login.');
  }

  /**
   * Completes a multi-factor sign-in. Unlike completeLogin(), a Firebase session is
   * guaranteed here (resolveSignIn succeeded), but the status subject may still hold the
   * stale pre-login null when this runs — sampling hasValidSession() at that instant
   * reports "no session" and strands the user on the challenge screen. Instead, wait for
   * the account bootstrap to publish the logged-in user before navigating.
   */
  private completeMfaLogin(): Promise<void> {
    return this.authService
      .status
      .pipe(
        skipWhile((user) => user === undefined || user === null),
        take(1),
        timeout(15000),
      )
      .toPromise()
      .then(() => this.ngZone.run(() => {
        this.router.navigate(['/']);
      }))
      .catch(() => this.ngZone.run(() => {
        this.mfaError = 'You are signed in, but your account failed to load. Please refresh the page.';
      }));
  }

  private completeLogin(): Promise<void> {
    return this.authService
      .hasValidSession()
      .toPromise()
      .then((session) => this.ngZone.run(() => {
        if (session) {
          this.router.navigate(['/']);
        } else {
          this.showInvalid();
        }
      }));
  }

  /** Firebase resolves outside the Angular zone, so re-enabling the buttons needs the zone. */
  private releaseRequest(): void {
    this.ngZone.run(() => {
      this.requestInProgress = false;
    });
  }

  /**
   * Reports a sign-in that did not produce a session. Every rejection but the anti-abuse
   * throttle is reported as bad credentials, which is what the screen has always said.
   *
   * The retry advice names a reload because on this screen there is nothing else: canSend
   * does not reopen without a reset(), and the login form has no control that calls one -
   * so a user who simply waits comes back to the same dead button and the same banner,
   * since invalidLogin is only cleared inside login(), which can no longer run. The wait is
   * still the remedy; the reload is only how the form comes back once it is over.
   */
  private showInvalid(e?: any): void {
    this.invalidLogin = this.authService.mapSignInError(
      e, 'Invalid username or password', 'Reload this page to try again once it has.');
  }

  private getParamValue(param: string): string {
    /**
     * '&amp;' is the escapeHtml equivalent for '&' this is added because regular '&' does not render properly in the
     * invite email
     */

    const url = this.router.url;
    if (url.includes('&amp;')) {
      const decodeUrl = decodeURIComponent(this.router.url);
      const cleanUrl = decodeUrl
        .split('amp;')
        .join('');
      const params = new HttpParams({fromString: cleanUrl.split('?')[1]});
      return params.get(param);
    }

    return this.route.snapshot.queryParamMap.get(param);
  }

  private getParamUser(): string {
    const decodeUrl = decodeURIComponent(this.router.url);
    const params = new HttpParams({fromString: decodeUrl.split('?')[1]});
    return params.get('user');
  }
}
